import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface SuspendedUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  suspensionReason: string;
}

interface SuspensionMessage {
  id: number;
  message: string;
  sender_type: 'user' | 'admin';
  sent_at: string;
  is_read: boolean;
  sender_username?: string;
}

const SuspendedUserModal: React.FC<SuspendedUserModalProps> = ({ 
  isOpen, 
  onClose, 
  suspensionReason 
}) => {
  const [messages, setMessages] = useState<SuspensionMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [canSendMore, setCanSendMore] = useState(true);
  const [messagesLeft, setMessagesLeft] = useState(3);

  useEffect(() => {
    if (isOpen) {
      loadMessages();
    }
  }, [isOpen]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSuspensionMessages();
      setMessages(response.messages);
      setCanSendMore(response.canSendMore);
      setMessagesLeft(response.messagesLeft);
    } catch (error) {
      console.error('Failed to load suspension messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !canSendMore) return;

    try {
      setLoading(true);
      const response = await apiService.sendSuspensionMessage(newMessage.trim());
      setNewMessage('');
      await loadMessages(); // Reload messages
      
      if (!response.canSendMore) {
        setCanSendMore(false);
        setMessagesLeft(0);
      } else {
        setMessagesLeft(response.messagesLeft);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="suspended-modal-overlay">
      <div className="suspended-modal-container">
        <div className="suspended-modal-content">
          <div className="suspended-modal-header">
            <h2>🚫 Account Suspended</h2>
            <button onClick={onClose} className="close-btn">✕</button>
          </div>

          <div className="suspension-info">
            <div className="suspension-reason-box">
              <h3>Suspension Reason:</h3>
              <p>{suspensionReason}</p>
            </div>

            <div className="suspension-instructions">
              <h3>What you can do while suspended:</h3>
              <ul>
                <li>🎮 <strong>Play built-in games</strong> - All game modes remain available</li>
                <li>📁 <strong>Upload game files</strong> - Test your own word lists locally</li>
                <li>📧 <strong>Contact admin</strong> - Send up to 3 messages per day to explain your case</li>
                <li>📬 <strong>Check responses</strong> - Admin replies will be sent to your email and appear here</li>
              </ul>
              <div className="suspension-limitations">
                <h4>⚠️ Suspended restrictions:</h4>
                <p>You cannot save games to your account or share them with other users until your suspension is resolved.</p>
              </div>
            </div>
          </div>

          <div className="messages-section">
            <h3>Conversation with Admin</h3>
            
            <div className="messages-container">
              {loading && messages.length === 0 ? (
                <p>Loading messages...</p>
              ) : messages.length === 0 ? (
                <p>No messages yet. Start the conversation by sending a message to the admin.</p>
              ) : (
                messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`suspension-message ${message.sender_type}`}
                  >
                    <div className="message-header">
                      <span className="sender">
                        {message.sender_type === 'admin' ? '🔧 Admin' : '👤 You'}
                      </span>
                      <span className="message-time">{formatDate(message.sent_at)}</span>
                    </div>
                    <div className="message-content">{message.message}</div>
                  </div>
                ))
              )}
            </div>

            <div className="message-input-section">
              <div className="message-quota">
                {canSendMore ? (
                  <span>You have <strong>{messagesLeft}</strong> message(s) left today</span>
                ) : (
                  <span className="quota-exceeded">
                    ⚠️ Daily message limit reached (3 messages). Try again tomorrow.
                  </span>
                )}
              </div>
              
              <div className="message-input-container">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={canSendMore ? "Type your message to the admin..." : "Daily message limit reached"}
                  disabled={!canSendMore || loading}
                  className="suspension-message-input"
                  rows={3}
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || !canSendMore || loading}
                  className="send-message-btn"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          </div>

          <div className="suspension-footer">
            <p>
              <strong>Important:</strong> This messaging system is for discussing your suspension only. 
              Misuse may result in permanent account termination.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuspendedUserModal;